<div class="px-4 sm:px-5 pt-6 sm:pt-10 pb-10 sm:pb-15 w-full max-w-[1280px] mx-auto">

  <!-- Breadcrumbs -->
  <nav class="flex items-center gap-2 text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6 flex-wrap">
    @for (crumb of breadcrumbs(); track crumb.label; let last = $last) {
      @if (!last) {
        <a [routerLink]="crumb.url" class="hover:text-gray-800 transition-colors">
          {{ crumb.label }}
        </a>
        <span>/</span>
      } @else {
        <span class="text-gray-800 font-medium">{{ crumb.label }}</span>
      }
    }
  </nav>

  @if (notFound()) {
    <div class="py-16 sm:py-20 text-center">
      <p class="text-gray-400 text-lg">Category not found.</p>
      <a routerLink="/browse-jobs" mat-stroked-button class="mt-4">
        Back to Browse Jobs
      </a>
    </div>
  } @else {

    <!-- Hero -->
    <div class="bg-gradient-to-b from-white to-blue-50 border border-blue-100 rounded-2xl p-5 sm:p-7 mb-6 sm:mb-7 shadow-xs">
      <div class="flex flex-col lg:flex-row lg:justify-between gap-5 lg:gap-4 items-start">
        <div class="max-w-3xl">
          <div class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-xs font-bold uppercase tracking-wider mb-3">
            Browse Jobs
          </div>

          <h1 class="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight text-gray-900 mt-0 mb-2.5">
            {{ pageContext().title }}
          </h1>

          <p class="text-gray-500 text-sm sm:text-base leading-relaxed mt-0 mb-0">
            {{ pageContext().summary }}
            @if (totalJobs() > 0) {
              {{ totalJobs() | number }} job{{ totalJobs() !== 1 ? 's' : '' }} found.
            }
          </p>
        </div>

        <div class="flex flex-col sm:flex-row gap-2.5 w-full sm:w-auto">
          <a routerLink="/jobs" mat-flat-button color="primary" class="w-full sm:w-auto">
            Search All Jobs
          </a>
          <a routerLink="/browse-jobs" mat-stroked-button class="w-full sm:w-auto">
            Browse All Categories
          </a>
        </div>
      </div>

      @if (activeFilters().length > 0) {
        <div class="flex flex-wrap gap-2 mt-5">
          @for (filter of activeFilters(); track filter.dimension; let i = $index) {
            <a
              [routerLink]="removalUrls()[i]"
              class="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 text-blue-600 text-sm font-semibold no-underline border border-blue-200 hover:bg-blue-100 transition-colors"
            >
              <span>{{ filter.dimensionLabel }}: {{ filter.valueLabel }}</span>
              <span class="font-bold">×</span>
            </a>
          }
        </div>
      }
    </div>

    <p class="max-w-4xl text-gray-500 text-sm sm:text-base leading-relaxed mb-5 sm:mb-6">
      Browse truck driving jobs with the filters already applied above. You can refine the current
      slice by adding another category from the sidebar, or remove a filter to broaden the results.
    </p>

    <!-- Layout -->
    <div class="grid grid-cols-1 lg:grid-cols-[minmax(0,1.5fr)_280px] gap-6 lg:gap-8 items-start">

      <!-- Results -->
      <div class="min-w-0">
        @if (loading()) {
          <div class="flex justify-center items-center py-20">
            <mat-spinner diameter="48" />
          </div>
        } @else if (jobs().length === 0) {
          <div class="bg-white border border-gray-200 rounded-2xl p-8 sm:p-12 text-center">
            <div class="text-4xl mb-3">🚛</div>
            <div class="text-base font-bold text-gray-600 mb-2">No jobs found</div>
            <div class="text-sm text-gray-400 leading-relaxed">
              Try removing a filter above, or use the sidebar to browse a related category.
            </div>
          </div>
        } @else {
          <div class="text-sm text-gray-500 mb-4">
            Showing {{ jobs().length }} result{{ jobs().length !== 1 ? 's' : '' }}
            @if (currentPage() > 1) {
              — Page {{ currentPage() }}
            }
          </div>

          <div class="grid gap-4 sm:gap-5 items-stretch mb-10 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3">
            @for (job of jobs(); track job.id) {
              <app-job-search-card [job]="job" />
            }
          </div>

          <div class="flex justify-center items-center gap-3 flex-wrap">
            @if (currentPage() > 1) {
              <button mat-stroked-button (click)="goToPage(currentPage() - 1)">
                ← Previous
              </button>
            }

            <span class="text-sm text-gray-500">
              Page {{ currentPage() }} of {{ totalPages() }}
            </span>

            @if (currentPage() < totalPages()) {
              <button mat-flat-button color="primary" (click)="goToPage(currentPage() + 1)">
                Next →
              </button>
            }
          </div>
        }
      </div>

      <!-- Sidebar -->
      <div class="flex flex-col gap-4 lg:sticky lg:top-6">
        @if (activeFilters().length > 0) {
          <div class="bg-white border border-gray-200 rounded-2xl p-5">
            <div class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
              Current Slice
            </div>
            <div class="flex flex-col gap-1.5 text-sm text-gray-600">
              @for (filter of activeFilters(); track filter.dimension) {
                <div>
                  <span class="font-semibold">{{ filter.dimensionLabel }}:</span>
                  {{ filter.valueLabel }}
                </div>
              }
            </div>
          </div>
        }

        @for (section of sidebarSections(); track section.slug) {
          <div class="bg-white border border-gray-200 rounded-2xl p-5">
            <div class="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">
              {{ section.label }}
            </div>
            <div class="flex flex-col gap-1">
              @for (item of section.items; track item.slug) {
                <a
                  [routerLink]="item.url"
                  class="flex items-center justify-between gap-2 px-2.5 py-2 rounded-lg no-underline text-gray-700 text-sm hover:bg-blue-50 transition-colors"
                >
                  <span>{{ item.label }}</span>
                  <span class="shrink-0 bg-blue-50 text-blue-600 text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap">
                    {{ item.count | number }}
                  </span>
                </a>
              }
            </div>
          </div>
        }
      </div>

    </div>
  }

</div>
